import React, { useState, useEffect } from "react";
import { message, Button, Tooltip, Select } from "antd";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  MailOutlined,
  SendOutlined,
  HistoryOutlined,
  RedoOutlined,
} from "@ant-design/icons";
import {
  sendPromoEmailApi,
  getEmailHistoryApi,
  getUserMetricsApi,
} from "../../services/api";

const QUILL_MODULES = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ["bold", "italic", "underline"],
    [{ color: [] }, { background: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
};

// Fixed, rarely-changing role catalog (see RolesSeeder.java / AuthService.userRole() /
// VendorService.create() in kidsz) — not worth a dedicated backend endpoint for this.
const ROLE_OPTIONS = ["User", "Vendor", "Sales", "Admin", "Super Admin"];

// Quill's "empty" content isn't an empty string — it's a lone <p><br></p>.
const isQuillEmpty = (html) => !html || html.replace(/<(.|\n)*?>/g, "").trim().length === 0;

// Derives an overall campaign status from the three counters EmailLog tracks.
// recipientCount is what kidsz queued; success/failureCount are filled in
// live as BulkEmailResultConsumer processes each result — the gap between
// (recipientCount) and (success+failure) is still in flight.
function campaignStatus(s) {
  const recipients = s.recipientCount ?? 0;
  const success = s.successCount ?? 0;
  const failure = s.failureCount ?? 0;
  const accounted = success + failure;
  if (recipients === 0) return { label: "No recipients", color: "#9ca3af", bg: "#f3f4f6" };
  if (accounted < recipients) return { label: "Pending", color: "#92400e", bg: "#fef3c7" };
  if (failure === 0) return { label: "Sent", color: "#065f46", bg: "#d1fae5" };
  if (success === 0) return { label: "Failed", color: "#991b1b", bg: "#fee2e2" };
  return { label: "Partially Failed", color: "#9a3412", bg: "#ffedd5" };
}

export default function PromoEmailComposer() {
  const [subject, setSubject] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [roles, setRoles] = useState([]); // empty => all users with an email on file
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [approxRecipients, setApproxRecipients] = useState(null);

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await getEmailHistoryApi();
      setHistory(res.data?.data || []);
    } catch {
      message.error("Failed to load email history");
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
    getUserMetricsApi()
      .then((res) => setApproxRecipients(res.data?.data?.totalUsers ?? null))
      .catch(() => {});
  }, []);

  const handleResend = (item) => {
    setSubject(item.subject || "");
    setBodyHtml(item.bodyHtml || "");
    setRoles(item.targetRoles && item.targetRoles !== "All users" ? item.targetRoles.split(", ") : []);
    message.info("Loaded into composer — edit if needed, then send.");
  };

  const isEmpty = !subject.trim() || isQuillEmpty(bodyHtml);

  const handleSend = async () => {
    if (isEmpty) {
      message.warning("Please fill in both subject and message body.");
      return;
    }
    setLoading(true);
    try {
      const res = await sendPromoEmailApi({ subject: subject.trim(), bodyHtml, roles });
      const info = res.data?.data || "Email broadcast queued";
      message.success(info);
      fetchHistory();
      // Delivery happens asynchronously (queued -> email-service -> MSG91), so
      // the row just fetched above still shows 0/0 — refetch once more after
      // it's had time to actually go out.
      setTimeout(fetchHistory, 5000);
      setSubject("");
      setBodyHtml("");
      setRoles([]);
    } catch (err) {
      message.error(err?.response?.data?.data || "Failed to send email");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: "24px 0" }}>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, display: "flex", alignItems: "center", gap: 10 }}>
          <MailOutlined style={{ color: "#4a90d9" }} />
          Promotional Email
        </h2>
        <p style={{ margin: "4px 0 0", color: "#6b7280", fontSize: 13 }}>
          Compose and send a promotional email to users with an email on file
          {roles.length === 0 && approxRecipients != null ? ` (~${approxRecipients} users)` : ""}.
        </p>
      </div>

      <div style={{ display: "flex", gap: 24, alignItems: "flex-start", flexWrap: "wrap" }}>
        {/* ── Left: Compose ──────────────────────────────────────────────── */}
        <div style={{ flex: "1 1 480px", minWidth: 320 }}>
          <div style={{
            background: "#fff", borderRadius: 12,
            boxShadow: "0 1px 6px rgba(0,0,0,0.08)", padding: 20,
          }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", display: "block", marginBottom: 6 }}>
              SUBJECT
            </label>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="e.g. 🔥 Flash Sale — 50% Off This Weekend!"
              maxLength={120}
              style={{
                width: "100%", padding: "10px 12px", borderRadius: 8,
                border: "1.5px solid #e5e7eb", fontSize: 14,
                outline: "none", boxSizing: "border-box", marginBottom: 16,
                transition: "border-color 0.2s",
              }}
              onFocus={(e) => (e.target.style.borderColor = "#4a90d9")}
              onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
            />

            <label style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", display: "block", marginBottom: 6 }}>
              SEND TO
            </label>
            <Select
              mode="multiple"
              allowClear
              value={roles}
              onChange={setRoles}
              placeholder="All users (default) — pick one or more roles to narrow it down"
              style={{ width: "100%", marginBottom: 16 }}
              options={ROLE_OPTIONS.map((role) => ({ label: role, value: role }))}
            />

            <label style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", display: "block", marginBottom: 6 }}>
              MESSAGE
            </label>
            <div style={{ marginBottom: 20 }}>
              <ReactQuill
                theme="snow"
                value={bodyHtml}
                onChange={setBodyHtml}
                modules={QUILL_MODULES}
                placeholder="Write your promotional email content here..."
                style={{ background: "#fff" }}
              />
            </div>

            <button
              onClick={handleSend}
              disabled={loading || isEmpty}
              style={{
                width: "100%", padding: "12px 0",
                background: loading || isEmpty ? "#e5e7eb" : "linear-gradient(135deg,#4a90d9,#2d5aa8)",
                color: loading || isEmpty ? "#9ca3af" : "#fff",
                border: "none", borderRadius: 10, fontSize: 15, fontWeight: 700,
                cursor: loading || isEmpty ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                transition: "0.2s",
              }}
            >
              {loading ? (
                <>
                  <span style={{
                    width: 16, height: 16, border: "2px solid #fff",
                    borderTopColor: "transparent", borderRadius: "50%",
                    display: "inline-block", animation: "promo-email-spin 0.6s linear infinite",
                  }} />
                  Sending...
                </>
              ) : (
                <><SendOutlined /> {roles.length === 0 ? "Send to All Users" : `Send to ${roles.join(", ")}`}</>
              )}
            </button>
          </div>
        </div>

        {/* ── Right: Preview + History ───────────────────────────────────── */}
        <div style={{ flex: "0 0 340px", minWidth: 300 }}>
          <div style={{
            background: "#fff", borderRadius: 12,
            boxShadow: "0 1px 6px rgba(0,0,0,0.08)", padding: 20, marginBottom: 20,
          }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 12 }}>
              Content Preview
            </div>
            <div style={{
              border: "1px solid #f3f4f6", borderRadius: 8, padding: 16,
              maxHeight: 260, overflowY: "auto", fontSize: 13,
            }}>
              <div style={{ fontWeight: 700, marginBottom: 8 }}>{subject || "Email subject"}</div>
              {isQuillEmpty(bodyHtml) ? (
                <div style={{ color: "#9ca3af" }}>Your message preview will appear here...</div>
              ) : (
                <div dangerouslySetInnerHTML={{ __html: bodyHtml }} />
              )}
            </div>
            <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 10 }}>
              This shows only your content — the sent email also wraps it in Kidodom's branded header/footer.
            </div>
          </div>

          <div style={{
            background: "#fff", borderRadius: 12,
            boxShadow: "0 1px 6px rgba(0,0,0,0.08)", padding: 20,
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "flex", alignItems: "center", gap: 6 }}>
                <HistoryOutlined /> Send History
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {historyLoading && <span style={{ fontSize: 11, color: "#9ca3af" }}>Loading…</span>}
                <Button size="small" onClick={fetchHistory} disabled={historyLoading}>
                  Refresh
                </Button>
              </div>
            </div>

            {history.length === 0 ? (
              <div style={{ fontSize: 12, color: "#9ca3af" }}>No emails sent yet.</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10, maxHeight: 420, overflowY: "auto" }}>
                {history.map((s) => {
                  const status = campaignStatus(s);
                  return (
                    <div key={s.id} style={{
                      padding: "10px 12px", borderRadius: 8, background: "#f9fafb",
                      borderLeft: `3px solid ${status.color}`,
                      display: "flex", justifyContent: "space-between", gap: 8,
                    }}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
                          <span style={{
                            fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 10,
                            color: status.color, background: status.bg, flexShrink: 0,
                          }}>
                            {status.label.toUpperCase()}
                          </span>
                          <div style={{ fontSize: 13, fontWeight: 600, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {s.subject}
                          </div>
                        </div>
                        <div style={{ fontSize: 11, color: "#9ca3af" }}>
                          {s.targetRoles || "All users"} · {s.recipientCount ?? 0} queued · {s.sentAt ? new Date(s.sentAt).toLocaleString() : ""}
                        </div>
                        <div style={{ fontSize: 11, marginTop: 4, display: "flex", gap: 10 }}>
                          <span style={{ color: "#16a34a", fontWeight: 600 }}>✓ {s.successCount ?? 0} sent</span>
                          {(s.failureCount ?? 0) > 0 && (
                            <span style={{ color: "#dc2626", fontWeight: 600 }}>✗ {s.failureCount} failed</span>
                          )}
                          {(s.recipientCount ?? 0) > (s.successCount ?? 0) + (s.failureCount ?? 0) && (
                            <span style={{ color: "#9ca3af" }}>
                              ⏳ {s.recipientCount - (s.successCount ?? 0) - (s.failureCount ?? 0)} pending
                            </span>
                          )}
                        </div>
                        {s.lastError && (
                          <div style={{
                            fontSize: 11, color: "#991b1b", background: "#fee2e2",
                            border: "1px solid #fecaca", borderRadius: 6,
                            padding: "6px 8px", marginTop: 6, wordBreak: "break-word",
                          }}>
                            <b>Why it failed:</b> {s.lastError}
                          </div>
                        )}
                      </div>
                      <Tooltip title="Load into composer to resend">
                        <Button
                          size="small"
                          icon={<RedoOutlined />}
                          onClick={() => handleResend(s)}
                          style={{ flexShrink: 0 }}
                        />
                      </Tooltip>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`@keyframes promo-email-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
